import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { passportApi } from "../lib/passport-api";

type Phase = "verifying" | "verified" | "error";

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [phase, setPhase] = useState<Phase>("verifying");
  // React 18 StrictMode double-invokes effects in dev; the token is single-use, so the 2nd POST would 400.
  // A fire-once ref guards the request — and we DON'T gate setPhase on a per-effect "active" cleanup flag:
  // StrictMode's first cleanup would flip it false before the in-flight POST resolves, stranding the page on
  // "Verifying…". The single POST's result always updates the (live) component.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      setPhase("error");
      return;
    }
    (async () => {
      try {
        await passportApi.auth.verifyEmail(token);
        setPhase("verified");
      } catch {
        setPhase("error");
      }
    })();
  }, [token]);

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <Card>
          {phase === "verifying" && (
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-semibold">Verifying your email…</CardTitle>
              <CardDescription>One moment.</CardDescription>
            </CardHeader>
          )}
          {phase === "verified" && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-semibold">Email verified</CardTitle>
                <CardDescription>Thanks — your email address is confirmed.</CardDescription>
              </CardHeader>
              <CardContent>
                <Link
                  to="/login"
                  className="text-sm text-foreground underline-offset-4 hover:underline"
                >
                  Continue to sign in
                </Link>
              </CardContent>
            </>
          )}
          {phase === "error" && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-semibold">Verification failed</CardTitle>
                <CardDescription>
                  This verification link is missing, invalid, expired, or already used.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link
                  to="/login"
                  className="text-sm text-foreground underline-offset-4 hover:underline"
                >
                  Back to sign in
                </Link>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </main>
  );
}
